// routes.ts
import { type Component, lazy } from "solid-js";

export type AppRoute = {
  pathname: string;
  name: string;
  permission?: string;
  component?: Component;
  children?: AppRoute[];
};

function createRoute(
  pathname: string,
  name: string,
  permission?: string,
  component?: Component
): AppRoute {
  return { pathname, name, permission, component, children: [] };
}

function addChild(
  parent: AppRoute,
  suffix: string,
  name: string,
  permission?: string,
  component?: Component
): AppRoute {
  const child: AppRoute = {
    pathname: `${parent.pathname}${suffix}`,
    name,
    permission,
    component,
  };
  parent.children?.push(child);
  return child;
}

const Dashboard = lazy(() => import("./pages/dashboard"));
const Application = lazy(() => import("./pages/application"));
const Profile = lazy(() => import("./pages/profile"));
const Permissions = lazy(() => import("./pages/permission"));
const PermissionForm = lazy(() => import("./pages/permission/form"));
const Roles = lazy(() => import("./pages/role"));
const RoleForm = lazy(() => import("./pages/role/form"));
const Users = lazy(() => import("./pages/user"));
const UserForm = lazy(() => import("./pages/user/form"));
// const Companies = lazy(() => import("./pages/companies"));

export const DashboardRoutes = createRoute(
  "/",
  "Beranda",
  undefined,
  Dashboard
);
export const ApplicationsRoutes = createRoute(
  "/apps",
  "Aplikasi",
  "view_apps",
  Application
);

export const ProfileRoutes = createRoute(
  "/profile",
  "Informasi Pribadi",
  undefined,
  Profile
);

export const PermissionsRoutes = createRoute(
  "/permissions",
  "Izin Aplikasi",
  "view_permissions",
  Permissions
);
addChild(
  PermissionsRoutes,
  "/create",
  "Tambah Izin Aplikasi",
  "add_permission",
  PermissionForm
);
addChild(
  PermissionsRoutes,
  "/update/:id",
  "Edit Izin Aplikasi",
  "edit_permission",
  PermissionForm
);

export const RolesRoutes = createRoute(
  "/roles",
  "Hak Akses",
  "view_roles",
  Roles
);
addChild(RolesRoutes, "/create", "Tambah Hak Akses", "add_role", RoleForm);
addChild(RolesRoutes, "/update/:id", "Edit Hak Akses", "edit_role", RoleForm);

export const UsersRoutes = createRoute(
  "/users",
  "Pengguna",
  "view_users",
  Users
);
addChild(UsersRoutes, "/create", "Tambah Pengguna", "add_user", UserForm);
addChild(UsersRoutes, "/update/:id", "Edit Pengguna", "edit_user", UserForm);

// export const CompaniesRoutes = createRoute("/companies", "Perusahaan", undefined, Companies);

export const sideMenuRoutes: AppRoute[] = [
  DashboardRoutes,
  ApplicationsRoutes,
  PermissionsRoutes,
  RolesRoutes,
  UsersRoutes,
];

export const routes: AppRoute[] = [
  DashboardRoutes,
  ProfileRoutes,
  ApplicationsRoutes,
  PermissionsRoutes,
  RolesRoutes,
  UsersRoutes,
].filter((r) => r.component);

export function flattenRoutes(routes: AppRoute[]): AppRoute[] {
  const result: AppRoute[] = [];
  function traverse(route: AppRoute) {
    result.push(route);
    route.children?.forEach(traverse);
  }
  routes.forEach(traverse);
  return result;
}

// routes.ts
export function getRouteName(pathname: string): string | undefined {
  const flat = flattenRoutes(routes);

  // Cari route dengan path terpanjang yang cocok
  const match = flat
    .filter((r) => pathname.startsWith(r.pathname.split("/:")[0]))
    .sort((a, b) => b.pathname.length - a.pathname.length)[0];

  return match?.name;
}
